import { makeStyles } from '@material-ui/styles'
import clsx from 'clsx'
import { isNil } from 'ramda'
import React from 'react'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import SimpleLink from 'core/components/SimpleLink'
import Text from 'core/elements/Text'
import Theme from 'core/themes/model'

interface CrumbProps {
  name: string
  path: string
  active: boolean
  icon: string
  leftIcon?: React.ReactNode
  disabled?: boolean
}

export default function Crumb({
  icon,
  name,
  path,
  active,
  leftIcon,
  disabled = false,
}: CrumbProps) {
  const isLink = !isNil(path) && !active
  const hideEllipsis = !!leftIcon || active
  const classes = useStyles()
  const textContent = (
    <Text
      variant="subtitle2"
      noWrap
      className={clsx('breadcrumb-text', classes.crumbText, {
        [classes.crumbTextActive]: active,
        [classes.crumbTextDisabled]: disabled,
      })}
    >
      {leftIcon && leftIcon}
      <span>{name}</span>
    </Text>
  )

  return (
    <li
      className={clsx(classes.breadcrumbItem, {
        [classes.breadcrumbItemSelectable]: isLink,
      })}
      style={{
        gridTemplateColumns: hideEllipsis
          ? 'max-content max-content'
          : 'minmax(36px, max-content) max-content',
      }}
    >
      {isLink ? (
        <SimpleLink src={path} className={classes.crumbLink}>
          {textContent}
        </SimpleLink>
      ) : (
        textContent
      )}
      {!active && (
        <FontAwesomeIcon solid className={classes.icon}>
          {icon}
        </FontAwesomeIcon>
      )}
    </li>
  )
}

type BreadcrumbKeys = keyof Theme['components']['breadcrumb']
const useStyles = makeStyles<Theme>((theme) => ({
  breadcrumbItem: {
    display: 'grid',
    alignItems: 'center',
    justifyItems: 'start',
    gap: 8,
    padding: 0,
    margin: 0,
    cursor: 'unset',

    '&:hover .breadcrumb-text': {
      backgroundColor: `${theme.components.breadcrumb.hoverBackground} !important`,
    },
  },
  breadcrumbItemSelectable: {
    cursor: 'pointer',
  },
  icon: {
    fontSize: 14,
    color: theme.components.breadcrumb.text,
    height: 12,
    display: 'grid',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 2,
  },
  crumbLink: {
    display: 'grid',
    '& .simple-link-text': {
      display: 'grid',
    },
  },
  crumbText: {
    width: '100%',
    padding: 8,
    borderRadius: 4,
    boxSizing: 'border-box',
    lineHeight: 1.25,
    color: theme.components.breadcrumb.text,
    '& > i': {
      marginRight: 8,
    },
  },
  crumbTextActive: {
    color: theme.components.breadcrumb.activeText,
  },
  crumbTextDisabled: {
    color: theme.components.breadcrumb.disabledText,
  },
}))
